import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/es";
import { useTranslation } from "react-i18next";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import { sessionlist, grpplayerlist, playerpresent } from "./funtionsBD/Mainservice";
import { ensureCoachRoleForLiveTrainingEntry } from "../utils/liveTrainingNav";
import {
  isSessionFinalized,
  shouldEnterLiveTrainingAfterPresence,
  TODAY_CARD_LIVE_REDIRECT_LEAD_MS,
} from "../utils/liveTrainingSessionWindow";
import { persistLiveTrainingSession } from "../hooks/useLiveTrainingRedirect";
import { formatTrainingSessionLabel } from "../utils/formatTrainingSessionLabel";
import { isPlayerAccount, readActiveHomeRole, readStoredUser } from "../utils/homeRole";
import { userCanEditPlanningSession } from "../utils/planningAccess";
import { useNavigate } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./TodaySessions.css";

export const TodaySessions = ({
  onSessionClick,
  onDateSelected,
  groups,
  selectedgrp,
  playerHomeMode = false,
}) => {
  const [todaySessions, setTodaySessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionPlayers, setSessionPlayers] = useState({}); // Map of ts_id -> present players array
  const [sessionPlayerData, setSessionPlayerData] = useState({}); // Map of ts_id -> player data for current user
  const { t, i18n } = useTranslation();
  const user = readStoredUser();
  const homeRole = readActiveHomeRole(user);
  const isPlayerUi = playerHomeMode || isPlayerAccount(user, { homeRole });
  const navigate = useNavigate();
  const rawLang = (i18n.language || localStorage.getItem("i18nextLng") || "en")
    .split("-")[0]
    .toLowerCase();
  const currentlng = rawLang === "gr" ? "de" : rawLang === "sp" ? "es" : rawLang;
  const localeCode = currentlng || "en";

  const formatSessionParts = (dateValue, timeValue) => {
    const datePart = dateValue
      ? moment(dateValue).locale(localeCode).format("dddd D MMM")
      : "";
    const timeParsed = moment(timeValue, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
    const timePart = timeParsed.isValid()
      ? timeParsed.locale(localeCode).format("LT")
      : timeValue?.slice?.(0, 5) || "";
    return { datePart, timePart };
  };

  const playerInitials = (player) => {
    if (player.user_alias) return player.user_alias.slice(0, 2).toUpperCase();
    if (player.user_first_name && player.user_last_name) {
      return (player.user_first_name[0] + player.user_last_name[0]).toUpperCase();
    }
    if (player.user_first_name) return player.user_first_name.slice(0, 2).toUpperCase();
    if (player.user_last_name) return player.user_last_name.slice(0, 2).toUpperCase();
    return "?";
  };

  // Check for selected session in localStorage
  useEffect(() => {
    const storedSession = JSON.parse(localStorage.getItem("rsessn"));
    if (storedSession && storedSession.ts_id) {
      setSelectedSessionId(storedSession.ts_id);
    } else {
      setSelectedSessionId(null);
    }

    // Listen for storage changes to update selection
    const handleStorageChange = () => {
      const updatedSession = JSON.parse(localStorage.getItem("rsessn"));
      setSelectedSessionId(updatedSession?.ts_id || null);
    };

    window.addEventListener("storage", handleStorageChange);
    // Also listen for custom event in case localStorage is updated in same window
    window.addEventListener("sessionSelected", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sessionSelected", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    fetchTodaySessions();
  }, [groups, selectedgrp, playerHomeMode]);

  // Listen for session creation events to refresh the list
  useEffect(() => {
    const handleSessionCreated = () => {
      console.log("🔄 TodaySessions - Session created event received, refreshing list...");
      fetchTodaySessions();
    };

    window.addEventListener("sessionCreated", handleSessionCreated);
    
    return () => {
      window.removeEventListener("sessionCreated", handleSessionCreated);
    };
  }, [groups, selectedgrp]);

  const getGroupIds = () => {
    const ids = [];
    const pushId = (id) => {
      if (id) ids.push(id);
    };
    if (Array.isArray(groups)) {
      groups.forEach((g) => pushId(g?.groupDetails?.gr_id || g?.gr_id));
    }
    if (playerHomeMode && ids.length === 0) {
      try {
        const stored = JSON.parse(localStorage.getItem("grpinf") || "null");
        pushId(stored?.groupDetails?.gr_id || stored?.gr_id);
      } catch {
        /* ignore */
      }
      pushId(user?.gr_id);
      if (Array.isArray(user?.groups)) {
        user.groups.forEach((g) => pushId(g?.gr_id || g?.groupDetails?.gr_id));
      }
    }
    return [...new Set(ids.filter(Boolean))];
  };
  
  // Helper function to get group name by group ID
  const getGroupName = (groupId) => {
    if (!groups || !Array.isArray(groups)) return "";
    const group = groups.find(
      (g) => (g?.groupDetails?.gr_id || g?.gr_id) === groupId
    );
    return group?.groupDetails?.gr_name || group?.gr_name || "";
  };

  const mapSession = (s, groupId) => {
    const tsId = s?.ts_id || s?.id;
    const tsNumber = s?.ts_session_number;
    const teStateId = s?.state_te?.st_id || s?.tbl_state_te?.st_id || s?.te_state;
    const state =
      teStateId === "st202406071447279903"
        ? "finalized"
        : teStateId === "st202406071447279901"
        ? "running"
        : teStateId === "st202406071447279902"
        ? "paused"
        : "scheduled";

    return {
      ...s,
      id: tsId,
      name: tsNumber
        ? (s?.ts_owner_player_id
            ? `Session #P${String(tsNumber).padStart(3, "0")}`
            : `Session #${tsNumber}`)
        : s?.name,
      ts_id: s?.ts_id || tsId,
      ts_date: s?.ts_date,
      ts_start_time: s?.ts_start_time,
      state,
      _groupId: groupId,
      _groupName: getGroupName(groupId), // Store group name with session
    };
  };

  const fetchTodaySessions = async () => {
    try {
      setLoading(true);

      const groupIds = getGroupIds();
      if (groupIds.length === 0 && !playerHomeMode) {
        setTodaySessions([]);
        setLoading(false);
        return;
      }

      // Fetch sessions for all groups on today's date only
      const todayKey = moment().format("YYYY-MM-DD");
      const listParams = {
        selected_date: todayKey,
        include_hr: false,
      };
      if (groupIds.length) listParams.gr_ids = groupIds;
      const res = await sessionlist(listParams);
      const list = res?.data?.group || [];
      const all = list.map((s) => mapSession(s, s.gr_id || s._groupId));

      // Deduplicate by ts_id
      const byId = new Map();
      all.forEach((s) => {
        if (!s?.ts_id) return;
        if (!byId.has(s.ts_id)) byId.set(s.ts_id, s);
      });

      const deduped = Array.from(byId.values());
      const today = moment().startOf("day");
      const todayOnly = deduped.filter((s) => {
        if (!s.ts_date) return false;
        return moment(s.ts_date).isSame(today, "day");
      });

      // Sort chronologically: first by date, then by start time
      todayOnly.sort((a, b) => {
        // First compare dates
        const dateA = moment(a.ts_date);
        const dateB = moment(b.ts_date);
        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        
        // If dates are equal, compare by start time
        const timeA = a.ts_start_time || "";
        const timeB = b.ts_start_time || "";
        return timeA.localeCompare(timeB);
      });

      setTodaySessions(todayOnly);
      
      // Fetch present players for each session
      await fetchSessionPlayers(todayOnly);
      
      // Fetch player data for current user for each session (if role is player)
      if (isPlayerUi) {
        const playerDataMap = {};
        for (const session of todayOnly) {
          try {
            const groupId = session.gr_id || session._groupId || selectedgrp?.groupDetails?.gr_id;
            if (!groupId) continue;
            const playerParams = {
              ts_id: session.ts_id,
              gr_id: groupId,
            };
            const playerResponse = await grpplayerlist(playerParams);
            const allPlayers = playerResponse.data.data || [];
            const meData = allPlayers.find((p) => p.user_id === user?.user_id);
            if (meData) {
              playerDataMap[session.ts_id] = meData;
            }
          } catch (error) {
            console.error(`Error fetching player data for session ${session.ts_id}:`, error);
          }
        }
        setSessionPlayerData(playerDataMap);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("❌ Error fetching today sessions:", error);
      setLoading(false);
      setTodaySessions([]);
    }
  };

  const fetchSessionPlayers = async (sessions) => {
    const playersMap = {};
    
    // Fetch players for each session in parallel
    const playerPromises = sessions.map(async (session) => {
      const grId = session.gr_id || session._groupId;
      if (!grId || !session.ts_id) return;
      
      try {
        const response = await grpplayerlist({
          ts_id: session.ts_id,
          gr_id: grId,
        });
        
        // Filter only present players (presensee === true) - include all, not just with images
        let presentPlayers = (response.data.data || []).filter(
          (player) => player.presensee === true
        );
        
        // Sort players by ranking points (fltt_ranking_points) in descending order (highest first)
        // Players without ranking points will be placed at the end
        presentPlayers.sort((a, b) => {
          const aRanking = a.fltt_ranking_points || a.ranking_points || 0;
          const bRanking = b.fltt_ranking_points || b.ranking_points || 0;
          return bRanking - aRanking; // Descending order
        });
        
        playersMap[session.ts_id] = presentPlayers;
      } catch (error) {
        console.error(`Error fetching players for session ${session.ts_id}:`, error);
        playersMap[session.ts_id] = [];
      }
    });
    
    await Promise.all(playerPromises);
    setSessionPlayers(playersMap);
  };

  const handlepresence = async (session, presence) => {
    try {
      const playerData = sessionPlayerData[session.ts_id];
      if (!playerData) {
        console.error("Player data not found for session");
        return;
      }
      const groupId = session.gr_id || session._groupId || selectedgrp?.groupDetails?.gr_id;
      if (!groupId) {
        console.error("Group ID not found for session");
        return;
      }
      const params = {
        ts_id: session.ts_id,
        gr_id: groupId,
        ts_present: presence,
        player_id: playerData.player_id,
      };
      const response = await playerpresent(params);
      // Refresh player data for this session
      try {
        const playerParams = {
          ts_id: session.ts_id,
          gr_id: groupId,
        };
        const playerResponse = await grpplayerlist(playerParams);
        const allPlayers = playerResponse.data.data || [];
        const meData = allPlayers.find((p) => p.user_id === user?.user_id);
        if (meData) {
          setSessionPlayerData(prev => ({
            ...prev,
            [session.ts_id]: meData
          }));
        }
        // Also refresh present players list - include all present players, not just with images
        let presentPlayers = (playerResponse.data.data || []).filter(
          (player) => player.presensee === true
        );
        
        // Sort players by ranking points (fltt_ranking_points) in descending order
        presentPlayers.sort((a, b) => {
          const aRanking = a.fltt_ranking_points || a.ranking_points || 0;
          const bRanking = b.fltt_ranking_points || b.ranking_points || 0;
          return bRanking - aRanking; // Descending order
        });
        
        setSessionPlayers(prev => ({
          ...prev,
          [session.ts_id]: presentPlayers
        }));
      } catch (error) {
        console.error("Error refreshing player data:", error);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const selectSession = (session) => {
    setSelectedSessionId(session.id || session.ts_id);
    const sessionToStore = {
      ...session,
      gr_id: session.gr_id || session._groupId || null,
    };
    localStorage.setItem("rsessn", JSON.stringify(sessionToStore));
    if (onSessionClick) onSessionClick(sessionToStore);
    window.dispatchEvent(new CustomEvent("sessionSelected"));
    return sessionToStore;
  };

  const resolveSessionGroup = (session) => {
    const gid = session?.gr_id || session?._groupId;
    const match =
      gid && Array.isArray(groups)
        ? groups.find((g) => String(g?.groupDetails?.gr_id || g?.gr_id) === String(gid))
        : null;
    if (match?.groupDetails) return match;
    if (String(selectedgrp?.groupDetails?.gr_id || "") === String(gid || "")) return selectedgrp;
    return match ? { groupDetails: match } : selectedgrp;
  };

  /**
   * Single tap: finalized → session results; players → Live Training; staff →
   * Live Training when start is within the next hour (or already live),
   * otherwise Planning.
   */
  const openSessionTraining = (session) => {
    const sessionToStore = selectSession(session);
    if (isSessionFinalized(sessionToStore)) {
      navigate("/coach/session-results", {
        state: { session: sessionToStore, selectedgrp: resolveSessionGroup(sessionToStore) },
      });
      return;
    }
    if (isPlayerUi) {
      persistLiveTrainingSession(sessionToStore, selectedgrp);
      navigate("/live-training");
      return;
    }
    if (
      shouldEnterLiveTrainingAfterPresence(
        sessionToStore,
        Date.now(),
        TODAY_CARD_LIVE_REDIRECT_LEAD_MS
      )
    ) {
      if (userCanEditPlanningSession(user, user?.permissions || [])) {
        ensureCoachRoleForLiveTrainingEntry(user, user?.permissions || []);
      }
      persistLiveTrainingSession(sessionToStore, selectedgrp);
      navigate("/live-training");
      return;
    }
    navigate("/planning");
  };

  const sectionTitle = (
    <div className="metrics">
      <div className="metrics-info home-info hts__head">
        <h3>{t("todaySessions") || "Today's Training Sessions"}</h3>
        {todaySessions.length > 0 ? (
          <p className="hts__count">
            {todaySessions.length}{" "}
            {todaySessions.length === 1
              ? t("session") || "session"
              : t("sessions") || "sessions"}
          </p>
        ) : null}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="hts">
        {sectionTitle}
        <p className="hts__loading">{t("loading") || "Loading..."}</p>
      </div>
    );
  }

  if (todaySessions.length === 0) {
    return (
      <div className="hts">
        {sectionTitle}
        <p className="hts__empty">
          {t("noSessionsToday") || "No training sessions scheduled for today"}
        </p>
      </div>
    );
  }

  return (
    <div className="hts">
      {sectionTitle}
      <div
        className="hts__track"
        role="list"
        aria-label={t("todaySessions") || "Today's Training Sessions"}
      >
        {todaySessions.map((session, index) => {
          const isSelected = selectedSessionId === (session.id || session.ts_id);
          const stateId =
            session.tbl_state_te?.st_id || session.state_te?.st_id || session.te_state;
          let stateClass = "scheduled";
          if (stateId === "st202406071447279901") stateClass = "running";
          else if (stateId === "st202406071447279902") stateClass = "paused";
          else if (stateId === "st202406071447279903") stateClass = "finalized";
          const isFinalized = stateClass === "finalized";
          const presentPlayers = sessionPlayers[session.ts_id] || [];
          const maxAvatars = 6;
          const visiblePlayers = presentPlayers.slice(0, maxAvatars);
          const remainingCount = Math.max(0, presentPlayers.length - maxAvatars);
          const { datePart, timePart } = formatSessionParts(
            session.ts_date,
            session.ts_start_time
          );
          const durationMin = Number(session.ts_duration);
          const durationLabel =
            Number.isFinite(durationMin) && durationMin > 0
              ? t("duration_mins", { minutes: durationMin }) ||
                `${durationMin} ${t("min") || "min"}`
              : null;
          const title =
            session.name ||
            formatTrainingSessionLabel(session) ||
            `Session ${session.id?.substring(0, 8) || ""}`;
          const statusLabel =
            session.tbl_state_te?.[`st_name_${currentlng}`] ||
            session.state_te?.[`st_name_${currentlng}`] ||
            (stateClass === "finalized"
              ? t("finalized")
              : stateClass === "running"
                ? t("running")
                : stateClass === "paused"
                  ? t("paused")
                  : t("scheduled"));
          const playerData = sessionPlayerData[session.ts_id];
          const canRegister =
            isPlayerUi &&
            stateId === "st202406071447279900" &&
            playerData &&
            (playerData.presensee === false || playerData.presensee === null);

          return (
            <div
              key={session.ts_id || index}
              className="hts__slide"
              role="listitem"
            >
              <article
                className={`hts-card hts-card--${stateClass}${
                  isSelected ? " hts-card--active" : ""
                }`}
                role="button"
                tabIndex={0}
                aria-label={`${title}. ${statusLabel}${
                  durationLabel ? `. ${durationLabel}` : ""
                }. ${t("openTraining") || "Open training"}`}
                onClick={() => openSessionTraining(session)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openSessionTraining(session);
                  }
                }}
              >
                <div className="hts-card__rail" aria-hidden="true" />
                <div className="hts-card__body">
                  <div className="hts-card__top">
                    <div className="hts-card__when">
                      <span className="hts-card__time">{timePart || "—"}</span>
                      <span className="hts-card__meta">
                        {durationLabel ? (
                          <span className="hts-card__duration">{durationLabel}</span>
                        ) : null}
                        {datePart ? (
                          <span className="hts-card__date">{datePart}</span>
                        ) : null}
                      </span>
                    </div>
                    <span className="hts-card__status">{statusLabel}</span>
                  </div>
                  <div>
                    <h4 className="hts-card__title">{title}</h4>
                    {session._groupName ? (
                      <p className="hts-card__group" title={session._groupName}>
                        {session._groupName}
                      </p>
                    ) : null}
                  </div>
                  <div className="hts-card__players">
                    {presentPlayers.length > 0 ? (
                      <>
                        {visiblePlayers.map((player, idx) => {
                          const label =
                            player.user_alias ||
                            `${player.user_first_name || ""} ${player.user_last_name || ""}`.trim() ||
                            "Player";
                          const hasImage =
                            player.user_image &&
                            player.user_image !== null &&
                            player.user_image !== "";
                          return hasImage ? (
                            <img
                              key={player.player_id || player.user_id || idx}
                              className="hts-card__avatar"
                              src={player.user_image}
                              alt=""
                              title={label}
                              style={{ zIndex: visiblePlayers.length - idx }}
                            />
                          ) : (
                            <span
                              key={player.player_id || player.user_id || idx}
                              className="hts-card__avatar-fallback"
                              title={label}
                              style={{ zIndex: visiblePlayers.length - idx }}
                            >
                              {playerInitials(player)}
                            </span>
                          );
                        })}
                        {remainingCount > 0 ? (
                          <span className="hts-card__more">+{remainingCount}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="hts-card__players-empty">
                        {t("noPlayersPresent") || "No players marked present yet"}
                      </span>
                    )}
                  </div>
                  {canRegister ? (
                    <button
                      type="button"
                      className="hts-card__register"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlepresence(session, true);
                      }}
                    >
                      {t("register")}
                    </button>
                  ) : null}
                </div>
                <div className="hts-card__aside">
                  {isFinalized &&
                  (session.avg_hr || session.max_hr || session.min_hr) ? (
                    <div className="hts-card__hr" aria-label={t("heartRate") || "Heart rate"}>
                      <HeartBrokenIcon style={{ color: "#ef4444", fontSize: 18 }} />
                      <span>Avg {session.avg_hr || "—"}</span>
                      <span>Max {session.max_hr || "—"}</span>
                    </div>
                  ) : (
                    <span aria-hidden="true" />
                  )}
                  <span className="hts-card__go" aria-hidden="true">
                    <ChevronRightIcon fontSize="small" />
                  </span>
                </div>
              </article>
            </div>
          );
        })}
      </div>
    </div>
  );
};

